import { Wallet } from "lucide-react";
import { useId } from "react";
import { useController } from "react-hook-form";
import Flag from "react-world-flags";

import {
  ISO3_TO_ISO2_MAP,
  PAYOUT_WIZARD_COUNTRIES,
} from "@/modules/dashboard/dispersions/constants/dispersions.constants";
import { currencyFlagCode, currencySymbol } from "@/modules/dashboard/dispersions/utils/currencyDisplay";
import { CURRENCY_TO_DISPERSION_CODE } from "@/modules/dashboard/wallets/constants/wallets.constants";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { formatRail } from "@/shared/utils/railDisplay";

import type { CorridorOption } from "../../hooks/useBatchSetupOptions";
import { CrossCurrencyNotice } from "./CrossCurrencyNotice";

export interface BatchRouteFieldsProps {
  wallets: RuntimeWallet[];
  walletsLoading: boolean;
  walletsError: string | null;
  corridorOptions: CorridorOption[];
  /** Staff have not picked the organization yet, so there is nothing to choose from. */
  locked: boolean;
  disabled?: boolean;
}

const ROW_LABEL = "w-14 shrink-0 font-mono text-[10px] uppercase tracking-wider text-muted-foreground";
const FLAG = "h-3 w-[18px] shrink-0 rounded-[2px] object-cover";

function walletFlag(currency: string): string | null {
  const iso3 = CURRENCY_TO_DISPERSION_CODE[currency];
  return currencyFlagCode(currency) ?? (iso3 ? (ISO3_TO_ISO2_MAP[iso3] ?? null) : null);
}

/**
 * The `sourceCurrency` and `corridor` fields drawn like the send-money panel: one strip, from a wallet to a
 * corridor, each row a dropdown.
 */
export function BatchRouteFields({
  wallets,
  walletsLoading,
  walletsError,
  corridorOptions,
  locked,
  disabled = false,
}: BatchRouteFieldsProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  const { t: tMaintenance } = useTranslations("railMaintenance");
  const countryLabel = useCountryLabel();
  const fromId = useId();
  const toId = useId();
  const fromErrorId = useId();
  const toErrorId = useId();
  const source = useController({ name: "sourceCurrency" });
  const corridor = useController({ name: "corridor" });

  const walletOptions = wallets.map((wallet) => {
    const frozen = wallet.status !== "active";
    const flag = walletFlag(wallet.currency);
    const amount = `${currencySymbol(wallet.currency)} ${formatCurrencyAmount(
      wallet.balance_available,
      wallet.currency,
      activeLanguage,
    )}`.trim();
    return {
      value: wallet.currency,
      disabled: frozen,
      label: (
        <span className="flex w-full min-w-0 items-center gap-2">
          {flag ? (
            <Flag code={flag} className={FLAG} aria-hidden="true" />
          ) : (
            <Wallet aria-hidden="true" className="size-3.5 text-muted-foreground" />
          )}
          <span className="font-medium">{wallet.currency}</span>
          <span className="ml-auto truncate pl-3 font-mono text-xs text-muted-foreground tabular-nums">
            {frozen ? (
              t("setup_wallet_frozen")
            ) : (
              <>
                <span className="sm:hidden">{amount}</span>
                <span className="hidden sm:inline">{t("setup_wallet_available", { amount })}</span>
              </>
            )}
          </span>
        </span>
      ),
    };
  });

  const destinationOptions = corridorOptions.map((option) => {
    const fallback =
      PAYOUT_WIZARD_COUNTRIES.find((country) => country.code === option.country)?.name ?? option.country;
    return {
      value: option.value,
      disabled: !option.available,
      label: (
        <span className="flex w-full min-w-0 items-center gap-2">
          <Flag
            code={ISO3_TO_ISO2_MAP[option.country] ?? option.country}
            className={FLAG}
            aria-hidden="true"
          />
          <span className="truncate">
            {countryLabel(option.country, fallback)} · {formatRail(option.rail)}
          </span>
          {option.available ? (
            <span className="ml-auto hidden pl-3 font-mono text-xs text-muted-foreground sm:inline">
              {option.currency}
            </span>
          ) : (
            <span className="ml-auto truncate pl-3 font-mono text-xs text-muted-foreground">
              {option.inMaintenance ? tMaintenance("badge") : t("setup_corridor_unavailable")}
            </span>
          )}
        </span>
      ),
    };
  });

  const sourceCurrency = typeof source.field.value === "string" ? source.field.value : "";
  const corridorValue = typeof corridor.field.value === "string" ? corridor.field.value : "";
  const deliveryCurrency = corridorOptions.find((option) => option.value === corridorValue)?.currency;
  const sourceError = source.fieldState.error?.message;
  const corridorError = corridor.fieldState.error?.message;

  const hint = locked
    ? null
    : walletsLoading
      ? t("setup_wallet_loading")
      : (walletsError ?? (wallets.length === 0 ? t("setup_wallet_empty") : t("setup_corridor_description")));

  return (
    <div className="space-y-3">
      <div className="divide-y divide-border rounded-xl border border-border bg-card">
        <div className="flex items-center gap-3 px-3 py-2">
          <span id={fromId} className={ROW_LABEL}>
            {t("setup_route_from")}
          </span>
          <ColocaSelect
            value={sourceCurrency}
            onValueChange={(value) => {
              source.field.onChange(value);
              source.field.onBlur();
            }}
            options={walletOptions}
            placeholder={<span className="text-muted-foreground">{t("setup_wallet_placeholder")}</span>}
            disabled={locked || disabled || walletsLoading}
            className="min-w-0 flex-1"
            aria-labelledby={fromId}
            aria-invalid={!!sourceError}
            aria-describedby={sourceError ? fromErrorId : undefined}
          />
        </div>
        <div className="flex items-center gap-3 px-3 py-2">
          <span id={toId} className={ROW_LABEL}>
            {t("setup_route_to")}
          </span>
          <ColocaSelect
            value={corridorValue}
            onValueChange={(value) => {
              corridor.field.onChange(value);
              corridor.field.onBlur();
            }}
            options={destinationOptions}
            placeholder={<span className="text-muted-foreground">{t("setup_corridor_placeholder")}</span>}
            disabled={locked || disabled}
            className="min-w-0 flex-1"
            aria-labelledby={toId}
            aria-invalid={!!corridorError}
            aria-describedby={corridorError ? toErrorId : undefined}
          />
        </div>
      </div>

      {sourceError && (
        <p id={fromErrorId} className="text-xs text-destructive">
          {sourceError}
        </p>
      )}
      {corridorError && (
        <p id={toErrorId} className="text-xs text-destructive">
          {corridorError}
        </p>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}

      {sourceCurrency && deliveryCurrency && sourceCurrency !== deliveryCurrency && (
        <CrossCurrencyNotice sourceCurrency={sourceCurrency} deliveryCurrency={deliveryCurrency} />
      )}
    </div>
  );
}
